/**
 * LOS TURNOS DEL NEGOCIO Y QUIEN LOS ALTERNA (PANEL-HORARIOS-TURNOS-T1, modelo de Josse 25-sep, API de A: nexo-api 333).
 *
 * Dos cosas que el negocio define una vez y el sistema usa todos los dias:
 *   1. los turnos (temprano / tarde): las horas de cada uno por dia de la semana (1 = lunes ... 7 = domingo);
 *   2. la alternancia: quien cambia de turno cada semana. Se guarda UNA semana de referencia (un lunes) con el turno de
 *      esa semana; la siguiente le toca el otro, y asi. Es la MISMA cuenta que hace la base (horario_del_dia):
 *      ((lunes de la semana - semana_ancla) / 7) par -> el turno ancla; impar -> el otro.
 * Quien no alterna sigue con su plantilla de siempre (la pestaña Plantillas). Los descansos tambien siguen alli.
 *
 * Puro: sin DOM, sin red, sin reloj propio (se le pasa `hoy`).
 */

export const TURNOS = Object.freeze(['temprano', 'tarde']);
export const NOMBRE_TURNO = Object.freeze({ temprano: 'Temprano', tarde: 'Tarde' });
export const DIAS = Object.freeze([1, 2, 3, 4, 5, 6, 7]);
export const NOMBRE_DIA = Object.freeze({ 1: 'Lunes', 2: 'Martes', 3: 'Miércoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sábado', 7: 'Domingo' });

const leer = (o, ...k) => { for (const x of k) if (o && o[x] !== undefined && o[x] !== null) return o[x]; return null; };
const MS_DIA = 24 * 3600 * 1000;

/** 'H:mm' o 'HH:mm[:ss]' -> 'HH:mm'; lo que no es hora -> ''. */
export function horaCorta(h) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(h ?? '').trim());
  if (!m) return '';
  const hh = Number(m[1]), mm = Number(m[2]);
  if (hh > 23 || mm > 59) return '';
  return String(hh).padStart(2, '0') + ':' + m[2];
}

/** Una fecha (Date o texto 'YYYY-MM-DD...') -> 'YYYY-MM-DD' en calendario, sin zona (la base guarda date). */
export function fechaISO(f) {
  if (f instanceof Date && !Number.isNaN(f.getTime())) {
    return f.getFullYear() + '-' + String(f.getMonth() + 1).padStart(2, '0') + '-' + String(f.getDate()).padStart(2, '0');
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(f ?? ''));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

const aDia = (iso) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d) / MS_DIA; };
const deDia = (n) => { const d = new Date(n * MS_DIA); return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0') + '-' + String(d.getUTCDate()).padStart(2, '0'); };

/** 1 = lunes ... 7 = domingo (ISO), de una fecha 'YYYY-MM-DD'. */
export function diaISO(iso) { const w = new Date(aDia(iso) * MS_DIA).getUTCDay(); return w === 0 ? 7 : w; }

/** El lunes de la semana de esa fecha. */
export function lunesDe(iso) { return deDia(aDia(iso) - (diaISO(iso) - 1)); }

export const otroTurno = (t) => (t === 'temprano' ? 'tarde' : 'temprano');

/**
 * El turno que le toca a una alternancia la semana de `fecha`. null si no aplica (inactiva, o la fecha es anterior al
 * ancla: la base tampoco la usa antes de semana_ancla).
 */
export function turnoDeLaSemana(alt, fecha) {
  if (!alt || !alt.activo || !alt.semanaAncla || !TURNOS.includes(alt.turnoAncla)) return null;
  const f = fechaISO(fecha);
  if (!f || f < alt.semanaAncla) return null;
  const semanas = Math.floor((aDia(lunesDe(f)) - aDia(alt.semanaAncla)) / 7);
  return semanas % 2 === 0 ? alt.turnoAncla : otroTurno(alt.turnoAncla);
}

/**
 * Lo que respondio GET horario-turnos -> lo que pinta la vista.
 * { estado: 'ok', turnos: {temprano:{1:{...}}, tarde:{...}}, alternancias:[...] } | { estado: 'error', causa }
 */
export function adaptarTurnos(res, { env = '' } = {}) {
  if (!res || res.ok !== true) return { estado: 'error', causa: causaDelFallo(res, { env, leyendo: true }) };
  const d = res.data?.data ?? res.data?.Data ?? res.data ?? {};
  const lt = leer(d, 'turnos', 'Turnos'), la = leer(d, 'alternancias', 'Alternancias');
  if (!Array.isArray(lt) || !Array.isArray(la)) return { estado: 'error', causa: 'La API respondió algo que no supe leer.' };
  const turnos = { temprano: {}, tarde: {} };
  for (const t of lt) {
    const turno = String(leer(t, 'turno', 'Turno') ?? '').trim().toLowerCase();
    const dia = Number(leer(t, 'diaSemana', 'DiaSemana'));
    if (!TURNOS.includes(turno) || !DIAS.includes(dia)) continue;
    turnos[turno][dia] = {
      horaEntrada: horaCorta(leer(t, 'horaEntrada', 'HoraEntrada')),
      horaInicioAlmuerzo: horaCorta(leer(t, 'horaInicioAlmuerzo', 'HoraInicioAlmuerzo')),
      horaFinAlmuerzo: horaCorta(leer(t, 'horaFinAlmuerzo', 'HoraFinAlmuerzo')),
      horaSalida: horaCorta(leer(t, 'horaSalida', 'HoraSalida'))
    };
  }
  const alternancias = la.map((a) => ({
    colaboradorId: String(leer(a, 'colaboradorId', 'ColaboradorId') ?? ''),
    nombre: String(leer(a, 'nombre', 'Nombre') ?? 'Sin nombre'),
    semanaAncla: fechaISO(leer(a, 'semanaAncla', 'SemanaAncla')),
    turnoAncla: String(leer(a, 'turnoAncla', 'TurnoAncla') ?? '').trim().toLowerCase(),
    activo: leer(a, 'activo', 'Activo') !== false
  })).filter((a) => a.colaboradorId).sort((x, y) => x.nombre.localeCompare(y.nombre));
  return { estado: 'ok', turnos, alternancias };
}

/**
 * A quien se puede agregar a la alternancia: los colaboradores que todavia NO alternan (quien ya esta se edita en su
 * fila; ofrecerlo otra vez invita a pisar su semana de referencia sin verla). Por nombre.
 */
export function quienesPuedenEntrar(colaboradores, alternancias) {
  const ya = new Set((alternancias || []).map((a) => String(a.colaboradorId)));
  return (Array.isArray(colaboradores) ? colaboradores : [])
    .filter((c) => c && c.id && !ya.has(String(c.id)))
    .map((c) => ({ id: String(c.id), nombre: String(c.nombreCompleto ?? c.nombre_completo ?? c.nombre ?? '?') }))
    .sort((x, y) => x.nombre.localeCompare(y.nombre));
}

/** Las horas de un turno en un dia, en una linea: "08:00 – 16:00 · almuerzo 12:00–13:00"; sin cargar -> ''. */
export function lineaDelTurno(h) {
  if (!h || !h.horaEntrada || !h.horaSalida) return '';
  const alm = h.horaInicioAlmuerzo && h.horaFinAlmuerzo ? ` · almuerzo ${h.horaInicioAlmuerzo}–${h.horaFinAlmuerzo}` : '';
  return `${h.horaEntrada} – ${h.horaSalida}${alm}`;
}

/**
 * Esta semana y la siguiente, por persona que alterna: que turno le toca y a que horas (si el turno esta cargado ese
 * dia). Es lo que el gerente mira para saber que le dice el sistema a la tardanza.
 */
export function semanaDeCadaUno(vista, hoy) {
  const lunes = lunesDe(fechaISO(hoy));
  const siguiente = deDia(aDia(lunes) + 7);
  return (vista?.alternancias || []).map((a) => {
    const esta = turnoDeLaSemana(a, lunes);
    const prox = turnoDeLaSemana(a, siguiente);
    const dias = DIAS.map((d) => ({ dia: d, linea: esta ? lineaDelTurno(vista.turnos?.[esta]?.[d]) : '' }));
    let nota = '';
    if (!a.activo) nota = 'Alternancia apagada: usa su plantilla.';
    else if (!esta && a.semanaAncla > lunes) nota = `Empieza a alternar la semana del ${a.semanaAncla}.`;
    else if (esta && dias.every((x) => !x.linea)) nota = `El turno ${NOMBRE_TURNO[esta].toLowerCase()} no tiene horas cargadas.`;
    return { colaboradorId: a.colaboradorId, nombre: a.nombre, estaSemana: esta, proximaSemana: prox, lunes, dias, nota };
  });
}

/** Parejas: si la alternancia de dos personas cae en el mismo turno la misma semana, no se cubren (Brayan y Camilo). */
export function choques(filas) {
  const activos = (filas || []).filter((f) => f.estaSemana);
  const porTurno = { temprano: activos.filter((f) => f.estaSemana === 'temprano'), tarde: activos.filter((f) => f.estaSemana === 'tarde') };
  if (activos.length < 2 || (porTurno.temprano.length && porTurno.tarde.length)) return '';
  const t = porTurno.temprano.length ? 'temprano' : 'tarde';
  return `Esta semana todos los que alternan quedan en el turno ${NOMBRE_TURNO[t].toLowerCase()}: nadie cubre el otro. Revisa la semana de referencia.`;
}

// ---------------------------------------------------------------------------
// Lo que se manda: el cuerpo se arma AQUI, en camelCase (ASP.NET no liga snake_case, F5-bis), y se valida lo mismo
// que valida la API antes de gastar el viaje.
// ---------------------------------------------------------------------------

/** { cuerpo } o { error, campo }. */
export function cuerpoTurno({ turno, diaSemana, horaEntrada, horaInicioAlmuerzo, horaFinAlmuerzo, horaSalida } = {}) {
  const t = String(turno ?? '').trim().toLowerCase();
  if (!TURNOS.includes(t)) return { error: 'El turno es temprano o tarde.', campo: 'turno' };
  const dia = Number(diaSemana);
  if (!DIAS.includes(dia)) return { error: 'El día va de lunes a domingo.', campo: 'diaSemana' };
  const ent = horaCorta(horaEntrada), sal = horaCorta(horaSalida);
  if (!ent) return { error: 'Falta la hora de entrada.', campo: 'horaEntrada' };
  if (!sal) return { error: 'Falta la hora de salida.', campo: 'horaSalida' };
  const ia = String(horaInicioAlmuerzo ?? '').trim(), fa = String(horaFinAlmuerzo ?? '').trim();
  const iAlm = horaCorta(ia), fAlm = horaCorta(fa);
  if ((ia && !iAlm) || (fa && !fAlm)) return { error: 'Las horas del almuerzo van como HH:mm o vacías.', campo: 'horaInicioAlmuerzo' };
  if (Boolean(iAlm) !== Boolean(fAlm)) return { error: 'El almuerzo lleva inicio y fin, o ninguno.', campo: iAlm ? 'horaFinAlmuerzo' : 'horaInicioAlmuerzo' };
  if (iAlm && !(ent < iAlm && iAlm < fAlm && fAlm < sal)) return { error: 'El almuerzo tiene que caer dentro de la jornada.', campo: 'horaInicioAlmuerzo' };
  return { cuerpo: { turno: t, diaSemana: dia, horaEntrada: ent, horaInicioAlmuerzo: iAlm || null, horaFinAlmuerzo: fAlm || null, horaSalida: sal } };
}

/** { cuerpo } o { error, campo }. La semana de referencia se lleva a SU lunes (la API exige lunes). */
export function cuerpoAlternancia({ colaboradorId, semana, turnoAncla, activo = true } = {}) {
  const id = String(colaboradorId ?? '').trim();
  if (!id) return { error: 'Elige a la persona.', campo: 'colaboradorId' };
  const f = fechaISO(semana);
  if (!f) return { error: 'Elige una fecha de la semana de referencia.', campo: 'semanaAncla' };
  const t = String(turnoAncla ?? '').trim().toLowerCase();
  if (!TURNOS.includes(t)) return { error: 'El turno de esa semana es temprano o tarde.', campo: 'turnoAncla' };
  return { cuerpo: { colaboradorId: id, semanaAncla: lunesDe(f), turnoAncla: t, activo: activo !== false } };
}

/** Por que fallo, en idioma de persona: por causa y por ambiente (F3). */
export function causaDelFallo(res, { env = '', leyendo = false } = {}) {
  const code = res?.code;
  const det = res?.details || {};
  const codigoApi = det.code ?? det.Code;
  if (code === 403 || code === 'FORBIDDEN') return 'Solo el jefe o el administrador del negocio ve y cambia los turnos.';
  if (codigoApi === 'FICHA_NO_ES_DEL_NEGOCIO') return 'Esa persona no es de este negocio.';
  if (code === 400 || codigoApi === 'TURNO_INVALIDO' || codigoApi === 'ALTERNANCIA_INVALIDA') return det.error ?? det.Error ?? res?.error ?? 'La API no aceptó los datos.';
  if (env === 'dev') return 'Estás en el panel de desarrollo: pregunta a la API local, que puede no tener los turnos. En producción se revisa aparte.';
  if (code === 404) return leyendo ? 'La API todavía no tiene publicados los turnos. Cuando se publique, esta pestaña se enciende sola.' : 'La API todavía no tiene publicado el guardado de turnos.';
  return 'No pudimos hablar con la API ahora. Intenta de nuevo en un momento.';
}
